'use client';

import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import {
  Eye,
  Download,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Search,
  Filter,
  FileText,
  FileDown,
  X,
  FolderDown,
  RefreshCw,
} from 'lucide-react';
import type { SubmissionRow } from '@/app/admin-dashboard/components/AdminDashboardPageClient';
import { createClient } from '@/lib/supabase/client';

interface RecentSubmissionsTableProps {
  submissions?: SubmissionRow[];
  loading?: boolean;
  onRefresh?: () => void;
}

type SortKey = 'studentName' | 'submissionDate' | 'programme' | 'submissionStatus';

// ─────────────────────────────────────────────
// EXPORT MODAL
// ─────────────────────────────────────────────

interface ExportModalProps {
  submissions: SubmissionRow[];
  onClose: () => void;
}

function ExportModal({ submissions, onClose }: ExportModalProps) {
  const programmes = Array.from(new Set(submissions.map((s) => s.programme).filter(Boolean))).sort();
  const [selectedProgramme, setSelectedProgramme] = useState<string>('all');
  const [exportFormat, setExportFormat] = useState<'csv' | 'tsv'>('csv');

  const handleExport = () => {
    const rows = selectedProgramme === 'all'
      ? submissions
      : submissions.filter((s) => s.programme === selectedProgramme);

    const headers = [
      'Full Name', 'TRN-4', 'Email Address', 'Mailing Address',
      'Telephone Number', 'Programme', 'Cohort',
      'Document Upload Status', 'Submission Status', 'Submission Date',
    ];

    const sep = exportFormat === 'csv' ? ',' : '\t';
    const escape = (val: string) =>
      exportFormat === 'csv' ? `"${(val || '').replace(/"/g, '""')}"` : (val || '');

    const lines = [
      headers.map(escape).join(sep),
      ...rows.map((s) =>
        [
          s.studentName, s.trn4, s.email, s.mailingAddress,
          s.telephone, s.programme, s.cohort,
          s.docUploadStatus, s.submissionStatus.replace(/_/g, ' '), s.submissionDate,
        ].map(escape).join(sep)
      ),
    ];

    const content = lines.join('\n');
    const mimeType = exportFormat === 'csv' ? 'text/csv' : 'text/tab-separated-values';
    const ext = exportFormat === 'csv' ? 'csv' : 'tsv';
    const suffix = selectedProgramme === 'all' ? 'All_Programmes' : selectedProgramme.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const fileName = `ZivaDocuSecure_${suffix}_${new Date().toISOString().slice(0, 10)}.${ext}`;

    const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  const previewCount = selectedProgramme === 'all'
    ? submissions.length
    : submissions.filter((s) => s.programme === selectedProgramme).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-modal w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileDown size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Export Student Records</h3>
              <p className="text-xs text-muted-foreground">Export by programme with full contact details</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Select Programme</label>
            <select
              value={selectedProgramme}
              onChange={(e) => setSelectedProgramme(e.target.value)}
              className="w-full px-3 py-2.5 bg-muted border border-border rounded-lg text-sm text-foreground outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">All Programmes ({submissions.length} students)</option>
              {programmes.map((p) => {
                const count = submissions.filter((s) => s.programme === p).length;
                return (
                  <option key={p} value={p}>{p} ({count} student{count !== 1 ? 's' : ''})</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Export Format</label>
            <div className="flex gap-2">
              {(['csv', 'tsv'] as const).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setExportFormat(fmt)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    exportFormat === fmt ? 'bg-primary text-white border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {fmt.toUpperCase()}
                  <span className="block text-[10px] font-normal opacity-70">
                    {fmt === 'csv' ? 'Excel / Sheets' : 'Tab-separated'}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <FileDown size={14} className="text-primary flex-shrink-0" />
            <p className="text-xs text-primary font-medium">
              {previewCount} student record{previewCount !== 1 ? 's' : ''} will be exported
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm font-semibold text-muted-foreground bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors">
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={previewCount === 0}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} />
            Export {previewCount} Record{previewCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DOWNLOAD DOCUMENTS MODAL
// ─────────────────────────────────────────────

interface DownloadDocsModalProps {
  onClose: () => void;
}

function DownloadDocsModal({ onClose }: DownloadDocsModalProps) {
  const [selectedProgramme, setSelectedProgramme] = useState<string>('all');
  const [isDownloading, setIsDownloading] = useState(false);
  const [message, setMessage] = useState('');

  const allProgrammes = [
    'Strategic Management and Leadership Development Level 6',
    'Human Resource Management Level 5',
    'Human Resource Management Level 4',
    'Logistics Management Level 5',
    'Business Administration Level 5',
    'Business Administration Management Level 3',
    'Supervisory Management Level 3',
    'Small Business Management Level 4',
    'Training and Development Level 4',
    'TVET Instruction/Delivery Level 5',
    'Assessment Level 4',
    'Digital Marketing Management Level 4',
    'Early Childhood Development Level 4',
    'Construction Project Management Level 5',
  ];

  const handleDownload = async () => {
    setIsDownloading(true);
    setMessage('');
    try {
      const supabase = createClient();
      const { data: files, error } = await supabase.storage
        .from('student-documents')
        .list('', { limit: 1000, sortBy: { column: 'name', order: 'asc' } });

      if (error) {
        setMessage('Error accessing documents: ' + error.message);
        setIsDownloading(false);
        return;
      }

      if (!files || files.length === 0) {
        setMessage('No documents have been uploaded yet.');
        setIsDownloading(false);
        return;
      }

      const lines = [
        '"Student Folder","File Name","Download URL"',
        ...files.map((f) => {
          const { data: urlData } = supabase.storage.from('student-documents').getPublicUrl(f.name);
          return `"${f.name}","${f.name}","${urlData?.publicUrl || ''}"`;
        }),
      ];

      const content = lines.join('\n');
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = selectedProgramme === 'all' ? 'All_Programmes' : selectedProgramme.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      link.download = `ZivaDocuSecure_Documents_${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setMessage(`Document manifest exported (${files.length} files).`);
    } catch (err: any) {
      setMessage('Error: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-modal w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FolderDown size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Download Student Documents</h3>
              <p className="text-xs text-muted-foreground">Download documents uploaded by students, by programme</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Filter by Programme</label>
            <select
              value={selectedProgramme}
              onChange={(e) => setSelectedProgramme(e.target.value)}
              className="w-full px-3 py-2.5 bg-muted border border-border rounded-lg text-sm text-foreground outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">All Programmes</option>
              {allProgrammes.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3.5">
            <p className="text-xs font-semibold text-blue-800 mb-1">How it works</p>
            <p className="text-xs text-blue-700 leading-relaxed">
              This exports a CSV manifest of all student documents stored in the system, including direct download links. Open the CSV in Excel or Google Sheets to access each file.
            </p>
          </div>
          {message && (
            <div className={`p-3 rounded-lg border text-xs font-medium ${message.startsWith('Error') ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
              {message}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm font-semibold text-muted-foreground bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors">
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Preparing...
              </>
            ) : (
              <>
                <FolderDown size={14} />
                Download Document List
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN TABLE COMPONENT
// ─────────────────────────────────────────────

export default function RecentSubmissionsTable({ submissions = [], loading, onRefresh }: RecentSubmissionsTableProps) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('submissionDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDownloadDocsModal, setShowDownloadDocsModal] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = submissions
    .filter((s) => {
      const q = search.toLowerCase();
      const matchSearch =
        !search ||
        s.studentName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.trn4.includes(q) ||
        s.programme.toLowerCase().includes(q) ||
        s.cohort.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'all' || s.submissionStatus === filterStatus;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'studentName') return a.studentName.localeCompare(b.studentName) * dir;
      if (sortKey === 'submissionDate') return a.submissionDate.localeCompare(b.submissionDate) * dir;
      if (sortKey === 'programme') return a.programme.localeCompare(b.programme) * dir;
      if (sortKey === 'submissionStatus') return a.submissionStatus.localeCompare(b.submissionStatus) * dir;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === paginated.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginated.map((s) => s.id)));
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronUp size={12} className="text-muted-foreground/40" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'resubmission_required', label: 'Resubmit' },
  ];

  const startIdx = filtered.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIdx = Math.min(currentPage * rowsPerPage, filtered.length);

  return (
    <>
      {showExportModal && <ExportModal submissions={submissions} onClose={() => setShowExportModal(false)} />}
      {showDownloadDocsModal && <DownloadDocsModal onClose={() => setShowDownloadDocsModal(false)} />}

      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-border flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText size={15} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Recent Submissions</h3>
              <p className="text-xs text-muted-foreground">{filtered.length} of {submissions.length} records</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border text-sm min-w-[200px]">
              <Search size={13} className="text-muted-foreground flex-shrink-0" />
              <input
                type="text"
                placeholder="Search name, TRN, email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
              />
            </div>
            {/* Filter */}
            <div className="flex items-center gap-1.5 px-3 py-2 bg-muted rounded-lg border border-border">
              <Filter size={13} className="text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                className="bg-transparent text-xs text-foreground outline-none cursor-pointer"
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {/* Download Documents */}
            <button
              onClick={() => setShowDownloadDocsModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-foreground text-xs font-semibold rounded-lg hover:bg-secondary/80 border border-border transition-colors"
            >
              <FolderDown size={13} />
              Download Docs
            </button>
            {/* Export by Programme */}
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors"
            >
              <FileDown size={13} />
              Export by Programme
            </button>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedRows.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/20">
            <span className="text-sm font-semibold text-primary">{selectedRows.size} selected</span>
            <div className="flex items-center gap-2 ml-auto">
              <button className="btn-outline text-xs px-3 py-1.5">Send Reminder</button>
              <button
                onClick={() => setShowExportModal(true)}
                className="btn-outline text-xs px-3 py-1.5"
              >
                Export Selected
              </button>
              <button className="btn-danger text-xs px-3 py-1.5">Flag for Review</button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto scrollbar-thin">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-muted-foreground">Loading submissions...</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors whitespace-nowrap" onClick={() => handleSort('studentName')}>
                    <div className="flex items-center gap-1">Student <SortIcon col="studentName" /></div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">TRN-4</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors whitespace-nowrap" onClick={() => handleSort('programme')}>
                    <div className="flex items-center gap-1">Programme <SortIcon col="programme" /></div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">Cohort</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors whitespace-nowrap" onClick={() => handleSort('submissionDate')}>
                    <div className="flex items-center gap-1">Submitted <SortIcon col="submissionDate" /></div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors whitespace-nowrap" onClick={() => handleSort('submissionStatus')}>
                    <div className="flex items-center gap-1">Status <SortIcon col="submissionStatus" /></div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">Docs</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">Follow-Up</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((s, idx) => (
                  <tr
                    key={s.id}
                    className={`border-b border-border last:border-0 transition-colors hover:bg-muted/40 ${
                      idx % 2 === 0 ? '' : 'bg-muted/10'
                    } ${selectedRows.has(s.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(s.id)}
                        onChange={() => toggleRow(s.id)}
                        className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground text-sm">{s.studentName}</span>
                        <span className="text-xs text-muted-foreground">{s.email}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        ****{s.trn4}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-foreground font-medium max-w-[140px] block truncate" title={s.programme}>
                        {s.programme}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-muted-foreground">{s.cohort}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-muted-foreground font-mono">{s.submissionDate}</span>
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={s.submissionStatus} size="sm" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        {s.docsTotal > 0 ? (
                          <>
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full bg-green-500"
                                style={{ width: `${Math.round((s.docsVerified / s.docsTotal) * 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground font-mono">{s.docsVerified}/{s.docsTotal}</span>
                            {s.docsRejected > 0 && (
                              <span className="text-[10px] font-bold text-red-600">−{s.docsRejected}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {s.followUp ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          Required
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title="View student profile">
                          <Eye size={14} />
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Download PDF">
                          <Download size={14} />
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="More actions">
                          <MoreHorizontal size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <FileText size={36} className="text-muted-foreground/30 mb-3" />
              <p className="text-sm font-semibold text-foreground">No submissions found</p>
              <p className="text-xs text-muted-foreground mt-1">
                {submissions.length === 0
                  ? 'Student submissions will appear here once they complete their registration forms'
                  : 'Try adjusting your search or filter criteria'}
              </p>
              {onRefresh && submissions.length === 0 && (
                <button
                  onClick={onRefresh}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw size={14} />
                  Refresh Data
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="text-xs bg-card border border-border rounded-md px-2 py-1 text-foreground outline-none cursor-pointer"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{startIdx}–{endIdx} of {filtered.length}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded border border-border bg-card hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ‹
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded border border-border bg-card hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}